import type { ContextType } from './contextApi';

/**
 * How a shared context divides an expense recorded without an explicit
 * split. Mirrors `settings.split.rule` on the API.
 */
export const SPLIT_RULES = ['none', 'equal', 'weighted', 'ask'] as const;
export type SplitRule = (typeof SPLIT_RULES)[number];

export interface SplitSettings {
  rule: SplitRule;
  weights?: Record<string, number>;
}

/**
 * The rules the form lets someone pick. "weighted" needs a weight per member,
 * so it is only offered once the context exists and has members to weigh.
 */
export const PICKABLE_SPLIT_RULES: SplitRule[] = ['equal', 'none', 'ask', 'weighted'];

/**
 * The rule a new context starts from. Only a head start: a family that
 * splits everything down the middle picks "equal"; the group decides.
 */
export const suggestedSplitRule = (type: ContextType): SplitRule =>
  type === 'trip' || type === 'friends' ? 'equal' : 'none';

/** The rule stored on a context, with "none" for anything absent or unknown. */
export const splitRuleOf = (settings?: Record<string, any> | null): SplitRule => {
  const rule = settings?.split?.rule;
  return (SPLIT_RULES as readonly string[]).includes(rule) ? (rule as SplitRule) : 'none';
};

/**
 * The settings to send back with a new rule. Weights only make sense with
 * the weighted rule, so switching away drops them; new weights replace the
 * stored ones.
 */
export const withSplitRule = (
  settings: Record<string, any> | null | undefined,
  rule: SplitRule,
  weights?: Record<string, number>,
): Record<string, any> => {
  const next = { ...(settings ?? {}) };
  const previous: Partial<SplitSettings> = next.split ?? {};
  const kept = weights ?? previous.weights;
  next.split = rule === 'weighted' && kept ? { rule, weights: kept } : { rule };
  return next;
};

/** The stored weights of a context, or an empty map. */
export const splitWeightsOf = (settings?: Record<string, any> | null): Record<string, number> => {
  const weights = settings?.split?.weights;
  return weights && typeof weights === 'object' ? (weights as Record<string, number>) : {};
};

/** Typed weights ("2", "1,5", "") to numbers, keeping only the positive ones. */
export const parseWeights = (typed: Record<string, string>): Record<string, number> =>
  Object.fromEntries(
    Object.entries(typed)
      .map(([userId, value]) => [userId, parseFloat(String(value).replace(',', '.'))] as const)
      .filter(([, value]) => Number.isFinite(value) && value > 0),
  );

/** Each weight as a whole percentage of the total, for display next to the fields. */
export const weightPercentages = (weights: Record<string, number>): Record<string, number> => {
  const total = Object.values(weights).reduce((sum, value) => sum + value, 0);
  if (total <= 0) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(weights).map(([userId, value]) => [userId, Math.round((value / total) * 100)]),
  );
};

/** One member's part of `amount` under the weights, rounded down to the cent. */
export const weightedPart = (amount: number, weights: Record<string, number>, userId: string): number => {
  const total = Object.values(weights).reduce((sum, value) => sum + value, 0);
  const weight = weights[userId] ?? 0;
  if (!Number.isFinite(amount) || amount <= 0 || total <= 0 || weight <= 0) {
    return 0;
  }
  return Math.floor((amount * 100 * weight) / total) / 100;
};
